import React from "react";
import Icon from "components/AppIcon";
import Input from "components/ui/Input";

const ROLES = ["SUPER_ADMIN", "ADMIN", "OPERATOR", "CUSTOMER_USER"];
const ROLE_LABELS = {
  SUPER_ADMIN: "Super Administrador",
  ADMIN: "Administrador",
  OPERATOR: "Operador",
  CUSTOMER_USER: "Usuario Cliente",
};

const PERMISSIONS = [
  { key: "canApproveUsers", label: "Aprobar usuarios" },
  { key: "canSuspendUsers", label: "Suspender usuarios" },
  { key: "canUploadUpdates", label: "Cargar actualizaciones" },
  { key: "canAssignUpdates", label: "Asignar actualizaciones" },
  { key: "canViewReports", label: "Ver reportes" },
  { key: "canManageSystem", label: "Configurar sistema" },
  { key: "canManageOrgs", label: "Gestionar organizaciones" },
  { key: "canDownloadUpdates", label: "Descargar actualizaciones" },
];

const ROLE_PERMISSIONS = {
  SUPER_ADMIN: { canApproveUsers: true, canSuspendUsers: true, canUploadUpdates: true, canAssignUpdates: true, canViewReports: true, canManageSystem: true, canManageOrgs: true, canDownloadUpdates: true },
  ADMIN: { canApproveUsers: true, canSuspendUsers: true, canUploadUpdates: false, canAssignUpdates: true, canViewReports: true, canManageSystem: false, canManageOrgs: true, canDownloadUpdates: false },
  OPERATOR: { canApproveUsers: false, canSuspendUsers: false, canUploadUpdates: true, canAssignUpdates: true, canViewReports: true, canManageSystem: false, canManageOrgs: false, canDownloadUpdates: false },
  CUSTOMER_USER: { canApproveUsers: false, canSuspendUsers: false, canUploadUpdates: false, canAssignUpdates: false, canViewReports: false, canManageSystem: false, canManageOrgs: false, canDownloadUpdates: true },
};

const ROLE_COLORS = {
  SUPER_ADMIN: { bg: "rgba(15,118,110,0.12)", text: "var(--color-primary)" },
  ADMIN: { bg: "rgba(8,145,178,0.12)", text: "var(--color-secondary)" },
  OPERATOR: { bg: "rgba(217,119,6,0.12)", text: "var(--color-warning)" },
  CUSTOMER_USER: { bg: "rgba(107,114,128,0.12)", text: "var(--color-muted-foreground)" },
};

const UserManagementConfig = ({ config, onChange }) => {
  const handleChange = (field, value) => {
    onChange("userManagement", field, value);
  };

  return (
    <div className="space-y-6">
      {/* Registration Workflow */}
      <div className="rounded-xl border p-4 md:p-6" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(15,118,110,0.1)" }}>
            <Icon name="UserCheck" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>Flujo de Aprobación de Registro</h3>
            <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Configuración del proceso de activación de usuarios</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Días para revisión de solicitud"
            type="number"
            value={config?.approvalDays}
            onChange={(e) => handleChange("approvalDays", e?.target?.value)}
            placeholder="3"
            description="Plazo máximo para aprobar/rechazar"
          />
          <Input
            label="Notificación de recordatorio (días)"
            type="number"
            value={config?.reminderDays}
            onChange={(e) => handleChange("reminderDays", e?.target?.value)}
            placeholder="1"
            description="Recordatorio antes del vencimiento"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { key: "autoRejectExpired", label: "Rechazar automáticamente solicitudes vencidas" },
            { key: "notifyAdminOnRegister", label: "Notificar al administrador en cada registro" },
            { key: "requireNITValidation", label: "Validación obligatoria de NIT colombiano" },
            { key: "allowSelfRegistration", label: "Permitir auto-registro de organizaciones" },
          ]?.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg" style={{ backgroundColor: "var(--color-muted)" }}>
              <div
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-base"
                style={{
                  backgroundColor: config?.[key] ? "var(--color-primary)" : "transparent",
                  border: `2px solid ${config?.[key] ? "var(--color-primary)" : "var(--color-border)"}`,
                }}
                onClick={() => handleChange(key, !config?.[key])}
              >
                {config?.[key] && <Icon name="Check" size={12} color="white" strokeWidth={3} />}
              </div>
              <span className="text-sm font-caption" style={{ color: "var(--color-foreground)" }}>{label}</span>
            </label>
          ))}
        </div>
      </div>
      {/* Role Permission Matrix */}
      <div className="rounded-xl border p-4 md:p-6" style={{ backgroundColor: "var(--color-card)", borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(8,145,178,0.1)" }}>
            <Icon name="Shield" size={18} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="font-heading font-700 text-base" style={{ color: "var(--color-foreground)" }}>Matriz de Permisos por Rol</h3>
            <p className="text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>Vista de referencia de permisos RBAC del sistema</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 pr-4 font-caption font-600 text-xs" style={{ color: "var(--color-muted-foreground)" }}>Permiso</th>
                {ROLES?.map((role) => (
                  <th key={role} className="text-center py-2 px-2 min-w-[80px]">
                    <span className="inline-block px-2 py-1 rounded-md text-xs font-caption font-600" style={{ backgroundColor: ROLE_COLORS?.[role]?.bg, color: ROLE_COLORS?.[role]?.text }}>
                      {ROLE_LABELS?.[role]?.split(" ")?.[0]}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSIONS?.map((perm, idx) => (
                <tr key={perm?.key} style={{ backgroundColor: idx % 2 === 0 ? "var(--color-muted)" : "transparent" }}>
                  <td className="py-2 pr-4 font-caption text-xs rounded-l-lg" style={{ color: "var(--color-foreground)" }}>{perm?.label}</td>
                  {ROLES?.map((role) => (
                    <td key={role} className="text-center py-2 px-2">
                      {ROLE_PERMISSIONS?.[role]?.[perm?.key] ? (
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                      ) : (
                        <Icon name="XCircle" size={16} color="var(--color-muted-foreground)" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagementConfig;